/*
 * App update prompt engine.
 *
 * Compares the installed version against the backend release registry
 * (GET /api/reference/app-version — public, works pre-login) and drives the
 * AppUpdateModal host: prompt (gently, dismissable) when a newer version
 * exists, offer "remind me later" (hourly snooze) and "skip this version", and
 * force a non-dismissable modal ONLY when the release is server-flagged
 * `critical` (or the client is below `minSupportedVersion`). A skipped version
 * stops auto-prompting but stays reachable from the Settings "Update available"
 * row (getAppUpdateStatus). The "Update now" button opens the right store
 * for the install channel: App Store, TestFlight, Google Play or the Firebase
 * App Tester, with graceful fallbacks when a listing link is not configured.
 *
 * Everything is defensively guarded so it degrades gracefully across native,
 * web and Tauri builds; on web the prompt is disabled entirely (the web build
 * updates itself on reload).
 */

import { AppState, Linking, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import pkg from '../../package.json';
import { ApiConfig } from '../config/ApiConfig';

const API_URL = `${ApiConfig.apiURL}/api`;
const SKIPPED_KEY_PREFIX = 'appUpdateSkipped:';
const LAST_PROMPT_KEY = 'appUpdateLastPromptAt';
const FIRST_CHECK_DELAY_MS = 7 * 1000;
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const DEFAULT_REMIND_MINUTES = 60;
const FETCH_TIMEOUT_MS = 8 * 1000;

/**
 * fetch() with a hard timeout so a hung request never stalls the check or the
 * app-foreground path. Falls back to a plain fetch where AbortController is
 * unavailable.
 *
 * @param {string} url Request URL.
 * @param {object} [options] fetch options.
 * @returns {Promise<Response>} The fetch response.
 */
async function fetchWithTimeout(url, options = {}) {
    if (typeof AbortController === 'undefined') {
        return fetch(url, options);
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } finally {
        clearTimeout(timer);
    }
}

// Last-resort store targets when the backend registry has no link configured
// for the channel — they open the store/tester app itself.
const CHANNEL_FALLBACKS = {
    appstore: { url: 'itms-apps://apps.apple.com', fallbackUrl: 'https://apps.apple.com' },
    testflight: { url: 'itms-beta://', fallbackUrl: 'https://apps.apple.com/app/testflight/id899247664' },
    playstore: { url: 'market://details?id=org.happysolutions.app', fallbackUrl: 'https://play.google.com/store/apps/details?id=org.happysolutions.app' },
    tester: { url: 'https://appdistribution.firebase.dev', fallbackUrl: 'https://appdistribution.firebase.dev' },
};

/**
 * Compare two semver-ish version strings on their first three numeric parts.
 *
 * @param {string} a Left version.
 * @param {string} b Right version.
 * @returns {number} -1 when a < b, 0 when equal, 1 when a > b.
 */
export function compareSemver(a, b) {
    const pa = String(a || '0').split('.').map((n) => parseInt(n, 10) || 0);
    const pb = String(b || '0').split('.').map((n) => parseInt(n, 10) || 0);

    for (let i = 0; i < 3; i += 1) {
        if ((pa[i] || 0) < (pb[i] || 0)) return -1;
        if ((pa[i] || 0) > (pb[i] || 0)) return 1;
    }

    return 0;
}

/** Guarded lazy accessor for react-native-device-info (absent on web/Tauri). */
function getDeviceInfoModule() {
    try {
        // eslint-disable-next-line global-require
        const DeviceInfo = require('react-native-device-info');

        return DeviceInfo.default || DeviceInfo;
    } catch (_e) {
        return null;
    }
}

/**
 * Detect where this build was installed from, to pick the right store button.
 *
 * iOS: react-native-device-info reports 'AppStore' | 'TestFlight' | other
 * (sideloaded / Firebase App Distribution profiles report as other).
 * Android: the installer package is 'com.android.vending' for Google Play;
 * anything else (package installer, null) is treated as a tester build.
 *
 * @returns {Promise<'appstore'|'testflight'|'playstore'|'tester'|'web'>} Install channel.
 */
export async function detectInstallChannel() {
    if (Platform.OS === 'web') {
        return 'web';
    }

    const DeviceInfo = getDeviceInfoModule();
    let installer = '';

    try {
        installer = (await DeviceInfo?.getInstallerPackageName?.()) || '';
    } catch (_e) {
        installer = '';
    }

    if (Platform.OS === 'ios') {
        if (/testflight/i.test(installer)) return 'testflight';
        if (/appstore/i.test(installer)) return 'appstore';

        return 'tester';
    }

    return installer === 'com.android.vending' ? 'playstore' : 'tester';
}

/**
 * Resolve the store link for a channel from the backend registry, falling back
 * to the built-in store/tester targets when no listing link is configured.
 *
 * @param {Object.<string, {url: ?string, fallbackUrl: ?string}>} channels Registry channel map.
 * @param {string} channel Detected install channel.
 * @returns {{url: string, fallbackUrl: string}} Openable link pair.
 */
export function resolveStoreLink(channels, channel) {
    const fromServer = channels?.[channel] || {};
    const fallback = CHANNEL_FALLBACKS[channel] || CHANNEL_FALLBACKS.tester;

    return {
        url: fromServer.url || fallback.url,
        fallbackUrl: fromServer.fallbackUrl || fallback.fallbackUrl,
    };
}

/**
 * Fetch the release registry from the backend (public endpoint, no auth).
 *
 * @returns {Promise<?object>} `{ latest, maxSkips, remindEveryMinutes, channels }` or null on any failure.
 */
export async function fetchAppVersionInfo() {
    try {
        const response = await fetchWithTimeout(`${API_URL}/reference/app-version`, {
            headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) return null;

        const info = await response.json();

        return info && typeof info.latest === 'string' ? info : null;
    } catch (_e) {
        return null;
    }
}

/**
 * App Store channel enhancer: ask Apple's public lookup API for the live store
 * version so App Store users get prompted even before the backend registry is
 * bumped. Silently a no-op while the app has no public listing.
 *
 * @param {object} info Backend registry (mutated copy is returned).
 * @returns {Promise<object>} Registry with `latest`/appstore link raised to the store values when newer.
 */
export async function enhanceWithItunesLookup(info) {
    try {
        const DeviceInfo = getDeviceInfoModule();
        const bundleId = DeviceInfo?.getBundleId?.() || 'hu.happysolutions.app';
        const response = await fetchWithTimeout(`https://itunes.apple.com/lookup?bundleId=${encodeURIComponent(bundleId)}&country=hu`);

        if (!response.ok) return info;

        const data = await response.json();
        const listing = data?.results?.[0];

        if (!listing?.version) return info;

        const enhanced = { ...info, channels: { ...(info.channels || {}) } };

        if (compareSemver(listing.version, info.latest) > 0) {
            enhanced.latest = listing.version;
        }

        if (listing.trackViewUrl) {
            enhanced.channels.appstore = {
                url: listing.trackViewUrl,
                fallbackUrl: listing.trackViewUrl,
            };
        }

        return enhanced;
    } catch (_e) {
        return info;
    }
}

/**
 * Pure prompt-policy decision — extracted for tests.
 *
 * @param {object} opts
 * @param {string} opts.current Installed app version.
 * @param {object} opts.info Release registry (`latest`, `critical`, `minSupportedVersion`, `remindEveryMinutes`).
 * @param {boolean} opts.skipped Whether the user chose "Skip this version" for `info.latest`.
 * @param {number} opts.lastPromptAt Epoch ms of the last shown prompt (0 = never).
 * @param {number} opts.now Current epoch ms.
 * @returns {{updateAvailable: boolean, shouldPrompt: boolean, forced: boolean}} Decision.
 */
export function evaluatePromptState({ current, info, skipped, lastPromptAt, now }) {
    const updateAvailable = compareSemver(current, info?.latest) < 0;

    if (!updateAvailable) {
        return { updateAvailable: false, shouldPrompt: false, forced: false };
    }

    const remindMs = (Number.isFinite(info?.remindEveryMinutes) ? info.remindEveryMinutes : DEFAULT_REMIND_MINUTES) * 60 * 1000;
    // Forcing is server-driven now: a `critical` release, or a client older than
    // an explicit `minSupportedVersion` floor. (The old skip-count forcing is gone.)
    const belowFloor = typeof info?.minSupportedVersion === 'string'
        && info.minSupportedVersion
        && compareSemver(current, info.minSupportedVersion) < 0;
    const forced = Boolean(info?.critical) || Boolean(belowFloor);
    // A forced update always shows. Otherwise: never auto-prompt a version the
    // user explicitly skipped, and honour the hourly "remind me later" snooze.
    const due = !lastPromptAt || (now - lastPromptAt) >= remindMs;
    const shouldPrompt = forced || (!skipped && due);

    return { updateAvailable: true, shouldPrompt, forced };
}

/** @returns {Promise<boolean>} Whether the user chose "Skip this version" for `version`. */
export async function isVersionSkipped(version) {
    try {
        return (await AsyncStorage.getItem(`${SKIPPED_KEY_PREFIX}${version}`)) === '1';
    } catch (_e) {
        return false;
    }
}

/**
 * Record that the user skipped the given target version — the watcher stops
 * auto-prompting for it, but the Settings "Update available" row still lets
 * them update on demand (and a NEWER release re-arms the prompt).
 *
 * @param {string} version Target (latest) version being skipped.
 * @returns {Promise<void>}
 */
export async function recordSkippedVersion(version) {
    try {
        await AsyncStorage.setItem(`${SKIPPED_KEY_PREFIX}${version}`, '1');
    } catch (_e) { /* noop */ }
}

/**
 * Open the store for the resolved link pair: deep link first, HTTPS fallback.
 *
 * @param {{url: string, fallbackUrl: string}} link Link pair from resolveStoreLink.
 * @returns {Promise<void>}
 */
export async function openStoreLink(link) {
    if (!link?.url) return;

    try {
        await Linking.openURL(link.url);
    } catch (_e) {
        if (link.fallbackUrl && link.fallbackUrl !== link.url) {
            await Linking.openURL(link.fallbackUrl).catch(() => {});
        }
    }
}

/**
 * Full check: fetch registry, detect channel, apply the stored skip state.
 *
 * @param {object} [opts]
 * @param {number} [opts.now] Injectable clock for tests.
 * @returns {Promise<?object>} Prompt payload for the modal, or null when nothing to show.
 */
export async function evaluateAppUpdate({ now = Date.now() } = {}) {
    const channel = await detectInstallChannel();

    if (channel === 'web') return null;

    let info = await fetchAppVersionInfo();

    if (!info) return null;

    if (channel === 'appstore') {
        info = await enhanceWithItunesLookup(info);
    }

    const current = pkg.version || '0.0.0';
    const skipped = await isVersionSkipped(info.latest);
    let lastPromptAt = 0;

    try {
        lastPromptAt = parseInt(await AsyncStorage.getItem(LAST_PROMPT_KEY), 10) || 0;
    } catch (_e) { /* noop */ }

    const state = evaluatePromptState({ current, info, skipped, lastPromptAt, now });

    if (!state.shouldPrompt) return null;

    return {
        current,
        latest: info.latest,
        forced: state.forced,
        channel,
        link: resolveStoreLink(info.channels, channel),
    };
}

/**
 * On-demand status for the Settings "Update available" row — independent of the
 * skip/snooze gate so the user can always update when they choose. Never throws;
 * returns `{ updateAvailable: false }` when up to date, offline, or on web.
 *
 * @returns {Promise<{updateAvailable: boolean, current: string, latest?: string, forced?: boolean, channel?: string, link?: {url: string, fallbackUrl: string}}>}
 */
export async function getAppUpdateStatus() {
    const current = pkg.version || '0.0.0';

    try {
        const channel = await detectInstallChannel();

        if (channel === 'web') return { updateAvailable: false, current };

        let info = await fetchAppVersionInfo();

        if (!info) return { updateAvailable: false, current };

        if (channel === 'appstore') {
            info = await enhanceWithItunesLookup(info);
        }

        if (compareSemver(current, info.latest) >= 0) {
            return { updateAvailable: false, current, latest: info.latest };
        }

        const belowFloor = typeof info.minSupportedVersion === 'string'
            && info.minSupportedVersion
            && compareSemver(current, info.minSupportedVersion) < 0;

        return {
            updateAvailable: true,
            current,
            latest: info.latest,
            forced: Boolean(info.critical) || Boolean(belowFloor),
            channel,
            link: resolveStoreLink(info.channels, channel),
        };
    } catch (_e) {
        return { updateAvailable: false, current };
    }
}

// ---------------------------------------------------------------------------
// Modal host wiring (same pub/sub shape as CashRegisterQRModalService).
// ---------------------------------------------------------------------------

let activeUpdateModalHandler = null;

/**
 * Called by the AppUpdateModal host on mount to receive prompt payloads.
 *
 * @param {(payload: object) => Promise<'update'|'skip'>} handler Opens the modal, resolves with the user's choice.
 * @returns {() => void} Unregister function.
 */
export function registerAppUpdateModalHandler(handler) {
    activeUpdateModalHandler = handler;

    return () => {
        if (activeUpdateModalHandler === handler) {
            activeUpdateModalHandler = null;
        }
    };
}

let watcherStarted = false;
let promptOpen = false;

/** One check → prompt → bookkeeping round; never throws. */
async function runUpdateCheck() {
    if (promptOpen || !activeUpdateModalHandler) return;

    try {
        const payload = await evaluateAppUpdate();

        if (!payload) return;

        promptOpen = true;
        await AsyncStorage.setItem(LAST_PROMPT_KEY, String(Date.now())).catch(() => {});

        // The modal opens the store itself ("Update now" must work on every
        // press); this promise only reports the closing choice. In forced mode
        // it never resolves — the modal stays up and promptOpen stays true, so
        // the watcher cannot stack further prompts.
        const choice = await activeUpdateModalHandler(payload);

        // 'skip' → suppress THIS version's auto-prompt (the Settings row still
        // lets the user update on demand). 'remind' → nothing to persist:
        // LAST_PROMPT_KEY (set above) already defers the next auto-prompt by
        // remindEveryMinutes. 'update' → the store was already opened.
        if (choice === 'skip' && !payload.forced) {
            await recordSkippedVersion(payload.latest);
        }
    } catch (_e) {
        // Update prompting must never break the app.
    } finally {
        promptOpen = false;
    }
}

/**
 * Start the background watcher: first check shortly after boot, then on every
 * app-foreground and on a 5-minute interval; the hourly re-prompt cadence is
 * enforced inside evaluatePromptState via LAST_PROMPT_KEY.
 *
 * @returns {() => void} Stop/cleanup function.
 */
export function startAppUpdateWatcher() {
    if (watcherStarted || Platform.OS === 'web') {
        return () => {};
    }

    watcherStarted = true;

    const firstTimer = setTimeout(runUpdateCheck, FIRST_CHECK_DELAY_MS);
    const interval = setInterval(runUpdateCheck, CHECK_INTERVAL_MS);
    const appStateSub = AppState.addEventListener?.('change', (state) => {
        if (state === 'active') runUpdateCheck();
    });

    return () => {
        watcherStarted = false;
        clearTimeout(firstTimer);
        clearInterval(interval);
        appStateSub?.remove?.();
    };
}

export default {
    compareSemver,
    detectInstallChannel,
    resolveStoreLink,
    fetchAppVersionInfo,
    enhanceWithItunesLookup,
    evaluatePromptState,
    evaluateAppUpdate,
    getAppUpdateStatus,
    isVersionSkipped,
    recordSkippedVersion,
    openStoreLink,
    registerAppUpdateModalHandler,
    startAppUpdateWatcher,
};
